import { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  CubeIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import { GET_VARIANT_OPTION_GROUPS, GET_PRODUCT_VARIANTS, GET_OPTION_GROUPS } from '../graphql/product-queries'
import { DELETE_VARIANT_OPTION_GROUP } from '../graphql/mutations'
import { VariantOptionGroup, VariantOptionGroupFilters, ProductVariant, OptionGroup } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const VariantOptionGroupsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedVariantId, setSelectedVariantId] = useState<number | undefined>(undefined)
  const [selectedOptionGroupId, setSelectedOptionGroupId] = useState<number | undefined>(undefined)

  const filters: VariantOptionGroupFilters = {
    product_variant_id: selectedVariantId,
    option_group_id: selectedOptionGroupId
  }

  const { data: variantOptionGroupsData, loading: variantOptionGroupsLoading, error: variantOptionGroupsError, refetch } = useQuery(GET_VARIANT_OPTION_GROUPS, {
    variables: { filters }
  })

  const { data: variantsData, loading: variantsLoading } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { filters: { active: true } }
  })

  const { data: optionGroupsData, loading: optionGroupsLoading } = useQuery(GET_OPTION_GROUPS, {
    variables: { filters: { active: true } }
  })

  const [deleteVariantOptionGroup] = useMutation(DELETE_VARIANT_OPTION_GROUP, {
    onCompleted: () => {
      toast.success('Variant option group connection removed successfully')
      refetch()
    },
    onError: (error) => {
      toast.error(`Failed to remove connection: ${error.message}`)
    }
  })

  const variantOptionGroups = variantOptionGroupsData?.variantOptionGroups || []
  const variants = variantsData?.productVariants || []
  const optionGroups = optionGroupsData?.optionGroups || []

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const handleVariantFilterChange = (value: number | undefined) => {
    setSelectedVariantId(value)
  }

  const handleOptionGroupFilterChange = (value: number | undefined) => {
    setSelectedOptionGroupId(value)
  }

  const handleDelete = async (id: number, variantName: string, optionGroupName: string) => {
    if (window.confirm(`Are you sure you want to remove the connection between "${variantName}" and "${optionGroupName}"?`)) {
      try {
        await deleteVariantOptionGroup({
          variables: { id: Number(id) }
        })
      } catch (error) {
        // Error is handled in the mutation's onError callback
      }
    }
  }

  const filteredVariantOptionGroups = variantOptionGroups.filter((vog: VariantOptionGroup) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      vog.product_variant?.name.toLowerCase().includes(searchLower) ||
      vog.product_variant?.product?.name.toLowerCase().includes(searchLower) ||
      vog.option_group?.name.toLowerCase().includes(searchLower)
    )
  })

  if (variantOptionGroupsLoading || variantsLoading || optionGroupsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (variantOptionGroupsError) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading variant option groups</h3>
        <p className="text-gray-500 mb-6">{variantOptionGroupsError.message}</p>
        <button 
          onClick={() => refetch()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Variant Option Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage which option groups are available for each product variant
          </p>
        </div>
        <Link
          to="/variant-option-groups/new"
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Connection
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by variant, product, or option group..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Variant Filter */}
          <div className="min-w-0 flex-1 lg:max-w-xs">
            <select
              value={selectedVariantId || ''}
              onChange={(e) => handleVariantFilterChange(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Variants</option>
              {variants.map((variant: ProductVariant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.name} - {variant.product?.name}
                </option>
              ))}
            </select>
          </div>

          {/* Option Group Filter */}
          <div className="min-w-0 flex-1 lg:max-w-xs">
            <select
              value={selectedOptionGroupId || ''}
              onChange={(e) => handleOptionGroupFilterChange(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Option Groups</option>
              {optionGroups.map((group: OptionGroup) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Variant Option Groups Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Variant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Option Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selection Rules
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sort Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVariantOptionGroups.map((vog: VariantOptionGroup) => (
                <tr key={vog.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CubeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vog.product_variant?.name}</div>
                        <div className="text-sm text-gray-500">ID: {vog.product_variant_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vog.product_variant?.product?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <TagIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{vog.option_group?.name}</div>
                        <div className="text-sm text-gray-500">ID: {vog.option_group_id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Min: {vog.option_group?.min_select}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Max: {vog.option_group?.max_select}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vog.sort_order || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {vog.option_group?.active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <Link
                        to={`/products/${vog.product_variant?.product?.id}/variants/${vog.product_variant_id}`}
                        className="text-primary-600 hover:text-primary-900"
                        title="View variant"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/option-groups/${vog.option_group_id}`}
                        className="text-primary-600 hover:text-primary-900"
                        title="View option group"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(vog.id, vog.product_variant?.name || '', vog.option_group?.name || '')}
                        className="text-red-600 hover:text-red-900"
                        title="Remove connection"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredVariantOptionGroups.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔗</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No variant option group connections found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedVariantId || selectedOptionGroupId
              ? 'Try adjusting your search or filters'
              : 'Get started by connecting product variants to option groups'
            }
          </p>
          {!searchTerm && !selectedVariantId && !selectedOptionGroupId && (
            <Link to="/variant-option-groups/new" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Connection
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default VariantOptionGroupsPage
